// use-sign-out.ts — Auth suite, action
//
// Signs the current user out: clears React Query caches, calls supabase.auth.signOut,
// purges any leftover Supabase localStorage keys, resets local auth state, redirects
// to '/'. Per the protocol, components consume this via `invoke` + handler bag rather
// than awaiting.

'use client';

import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { supabase } from '@/lib/supabase';
import { setAuthSession } from '@/lib/authState';
import { useActionHook } from '@/lib/query/use-action-hook';
import type { ActionResult } from '@/types/async-protocol';

const SUPABASE_LOCAL_STORAGE_KEY_PREFIX = 'sb-';

export function useSignOut(): ActionResult<void, void> {
  const queryClient = useQueryClient();
  const router = useRouter();

  return useActionHook<void, void>(
    useMemo(
      () => ({
        mutationFn: async () => {
          await supabase.auth.signOut();
          Object.keys(localStorage).forEach((key) => {
            if (key.startsWith(SUPABASE_LOCAL_STORAGE_KEY_PREFIX)) {
              localStorage.removeItem(key);
            }
          });
          setAuthSession(null);
          router.replace('/');
        },
        onInvalidate: () => {
          // Nuke every cached query — the auth user is gone, nothing in the cache is valid.
          queryClient.clear();
        },
      }),
      [queryClient, router],
    ),
  );
}
